import { useCallback, useEffect, useState } from 'react'
import {
  Backdrop,
  CircularProgress,
  Grid,
  makeStyles,
  TextField,
  Avatar,
  Typography,
  Divider
} from '@material-ui/core'
import { validateEmptyField } from '../../common/validations'
import { getProduct } from '../../api/products.service'
import SaveOrCancelButtons from '../../components/saveOrCancelButtons'
import { useSelector } from 'react-redux'
import { productInputProps } from './products.data.keys'
import { genBase64, getInputValue } from '../../common/utils'
import FileInput from '../../components/FileInput'
import {
  getModifiedProduct,
  updateModifiedProduct,
  createModifiedProduct
} from '../../api/productModification.service'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import { DialogTitle } from '../../components/DialogTitle'

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  title: {
    justifySelf: 'end',
    margin: '-5px 15px 20px 5px'
  },
  commentContainer: {
    border: 'solid 1.5px #ff1744',
    backgroundColor: '#efefef',
    borderRadius: '10px',
    margin: '15px 0px',
    padding: '10px'
  },
  imagePreview: {
    cursor: 'pointer',
    maxWidth: '300px',
    maxHeight: '300px',
    minWidth: '250px',
    minHeight: '250px',
    objectFit: 'contain',
    '&:hover': {
      boxShadow: '0px 0px 11px 4px rgba(0,0,0,0.55)'
    }
  }
}))

const ProductForm = (props) => {
  const classes = useStyles()
  const providerSelected = useSelector((state) => state.provider)

  const productId = props.id
  const handleClose = props.onClose
  const afterSave = props.onSave
  const isModification = props.isModification === true
  const formTitle = isModification
    ? 'Modificación De Artículo'
    : productId === null
      ? 'Nuevo Artículo'
      : 'Solicitar Modificación De Artículo'
  const changeDescriptionTitle =
    productId !== null ? 'Comentario cambios del artículo' : 'Comentario creación del artículo'
  const changeDescriptionInstructions =
    productId !== null
      ? 'Indique los cambios que realizó en el artículo.'
      : 'Puede indicar un comentario sobre la creación del artículo.'

  const [loading, setLoading] = useState(false)

  const [stateForm, setStateForm] = useState({
    providerId: providerSelected.id,
    articleCode: productId,
    changeDescripcion: '',
    additionalInfo: '',
    videoUrl: '',
    imageUrl: '',
    imageFileName: '',
    imageBase64Content: null
  })

  const [files, setFiles] = useState({})
  const [filesError, setFilesError] = useState({})

  const [stateErrorForm, setStateErrorForm] = useState({
    ...(productId !== null ? { changeDescripcion: '' } : {})
  })

  const getProductData = useCallback(() => {
    setLoading(true)
    getProduct(productId).then((result) => {
      setStateForm({
        ...result.data,
        providerId: providerSelected.id,
        videoUrl: result.data.videoUrl === null ? '' : result.data.videoUrl,
        additionalInfo: result.data.additionalInfo === null ? '' : result.data.additionalInfo,
        changeDescripcion:
          result.data.changeDescripcion === null ? '' : result.data.changeDescripcion,
        imageFileName: '',
        imageBase64Content: null
      })

      setLoading(false)
    })
  }, [productId, providerSelected.id])

  const getProductModificationData = useCallback(() => {
    setLoading(true)
    const headers = {
      skipLog: true
    }
    getModifiedProduct(productId, headers).then((result) => {
      setStateForm({
        ...result.data,
        providerId: providerSelected.id,
        imageUrl: result.data.urlImage === null ? '' : result.data.urlImage,
        videoUrl: result.data.videoUrl === null ? '' : result.data.videoUrl,
        additionalInfo: result.data.additionalInfo === null ? '' : result.data.additionalInfo,
        changeDescripcion:
          result.data.changeDescripcion === null ? '' : result.data.changeDescripcion,
        imageFileName: '',
        imageBase64Content: null
      })

      setLoading(false)
    })
  }, [productId, providerSelected.id])

  useEffect(() => {
    if (productId !== undefined && productId !== null) {
      if (isModification) {
        getProductModificationData()
      } else {
        getProductData()
      }
    }
  }, [productId, isModification, getProductData, getProductModificationData])

  const handleInputChange = (event) => {
    const value = getInputValue(event.target)
    const name = event.target.name

    setStateErrorForm({
      ...stateErrorForm,
      [name]: ''
    })

    setStateForm({
      ...stateForm,
      [name]: value
    })
  }

  const validateForm = () => {
    let hasError = false
    let listError = {}

    for (const key in stateErrorForm) {
      if (Object.hasOwnProperty.call(stateForm, key)) {
        const value = stateForm[key]
        const error = validateEmptyField(value) ?? ''
        if (!hasError) {
          hasError = error !== ''
        }
        listError[key] = error
      }
    }

    setStateErrorForm({
      ...stateErrorForm,
      ...listError
    })

    if (!isModification && productId === null && !stateForm.urlProductCard)
      listError.productCard = validateEmptyField(files.productCardFileName) ?? ''

    setFilesError({
      ...filesError,
      ...listError
    })

    if (!hasError) {
      for (const key in listError) {
        if (Object.hasOwnProperty.call(listError, key)) {
          const element = listError[key]
          if (element !== '') {
            hasError = true
            break
          }
        }
      }
    }
    return !hasError
  }

  const handleSave = () => {
    if (!validateForm()) return
    if (isModification) {
      saveProductModification()
    } else {
      createProductModification()
    }
  }

  const createProductModification = () => {
    setLoading(true)
    createModifiedProduct({
      ...stateForm,
      ...files
    })
      .then((data) => {
        if (data && data.isAxiosError) return
        afterSave()
      })
      .finally(() => {
        setLoading(false)
        handleClose()
      })
  }

  const saveProductModification = () => {
    setLoading(true)
    updateModifiedProduct({
      ...stateForm,
      ...files
    })
      .then((data) => {
        if (data && data.isAxiosError) return
        afterSave()
      })
      .finally(() => {
        setLoading(false)
        handleClose()
      })
  }

  const loadFile = async (event) => {
    const name = event.target.name

    let fileNameReset = {}
    fileNameReset[`${name}FileName`] = undefined
    setFiles({
      ...files,
      ...fileNameReset
    })

    setFilesError({})

    if (event.target.files.length === 0) return

    const file = event.target.files[0]

    let dataFile = {}
    dataFile[`${name}FileName`] = file.name
    dataFile[`${name}Base64Content`] = await genBase64(file)
    setFiles({
      ...files,
      ...dataFile
    })
    return file.name
  }

  const openFileExplorer = () => {
    document.getElementById('image').click()
  }

  const loadImage = async (event) => {
    if (event.target.files.length === 0) return
    const file = event.target.files[0]
    if (['image/png', 'image/jpeg'].includes(file.type)) {
      setStateForm({
        ...stateForm,
        imageUrl: URL.createObjectURL(file),
        imageFileName: file.name,
        imageBase64Content: await genBase64(file)
      })
    } else {
      setStateForm({
        ...stateForm,
        imageUrl: '',
        imageFileName: '',
        imageBase64Content: null
      })
    }
  }

  const Title = (_) => (
    <h2>
      {formTitle}: {productId}
    </h2>
  )

  return (
    <div>
      <Grid container direction="column">
        <DialogTitle title={<Title />} />
        <Grid item>
          <Grid container direction="row" spacing={3}>
            {stateForm.state === 'RevisionProveedor' && (
              <Grid item xs={12} className={classes.commentContainer}>
                <div className={classes.rowTitle}>Comentario: </div>
                <div>{stateForm.comments}</div>
              </Grid>
            )}
            <Grid item xs={12} md={5}>
              <Typography>IMAGEN DE ARTÍCULO</Typography>
              <Divider className="mb-2" />
              <Typography color="primary" variant="caption" component="p" className="mb-3">
                Para agregar la imagen del artículo, basta con hacer clic sobre el cuadro rojo o la
                imagen existente del artículo, luego se abrirá el explorador de archivos.
              </Typography>
              {stateForm.imageUrl !== null && stateForm.imageUrl !== '' ? (
                <img
                  className={classes.imagePreview + ' d-flex mx-auto'}
                  alt={`img-product-${stateForm.id}`}
                  onClick={openFileExplorer}
                  src={stateForm.imageUrl}
                  onError={(event) => {
                    event.currentTarget.onerror = null
                    setStateForm({ ...stateForm, imageUrl: null })
                  }}
                />
              ) : (
                <Avatar
                  className={classes.imagePreview + ' bg-primary mx-auto '}
                  variant="rounded"
                  onClick={openFileExplorer}
                >
                  <ImageOutlinedIcon style={{ fontSize: 125 }} />
                </Avatar>
              )}
              <input
                type="file"
                accept="image/*"
                id="image"
                name="image"
                className="d-none"
                onChange={loadImage}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                  <Typography>FICHA DEL ARTÍCULO</Typography>
                  <Divider className="mb-2" />
                  <Typography color="primary" variant="caption" component="p" className="mb-2">
                    Seleccione un documento en el que se reflejen las características del artículo.
                  </Typography>
                  <FileInput
                    onLoadFile={loadFile}
                    inputName="productCard"
                    loading={loading}
                    inputError={filesError.productCard}
                    buttonLabel="Ficha Artículo"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography>DOCUMENTOS DEL ARTÍCULO</Typography>
                  <Divider className="mb-2" />
                  <Typography color="primary" variant="caption" component="p">
                    Los documentos no son obligatorios. Para otros documentos no mencionados,
                    utilice los botones “otros”.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FileInput
                    onLoadFile={loadFile}
                    inputName="label"
                    loading={loading}
                    buttonLabel="Etiqueta"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FileInput
                    onLoadFile={loadFile}
                    inputName="securityCard"
                    loading={loading}
                    buttonLabel="Ficha de Seguridad"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FileInput
                    onLoadFile={loadFile}
                    inputName="greenPoint"
                    loading={loading}
                    buttonLabel="Punto Verde"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FileInput
                    onLoadFile={loadFile}
                    inputName="sugar"
                    loading={loading}
                    buttonLabel="Azúcar"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FileInput
                    onLoadFile={loadFile}
                    inputName="additional1"
                    loading={loading}
                    buttonLabel="Otro 1"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FileInput
                    onLoadFile={loadFile}
                    inputName="additional2"
                    loading={loading}
                    buttonLabel="Otro 2"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography className="text-uppercase">{changeDescriptionTitle}</Typography>
              <Divider className="mb-2" />
              <Typography color="primary" variant="caption" component="p" className="mb-2">
                {changeDescriptionInstructions}
              </Typography>
              <TextField
                type="text"
                name="changeDescripcion"
                label="Comentario"
                multiline
                rows={6}
                variant="outlined"
                fullWidth
                value={stateForm.changeDescripcion}
                inputProps={productInputProps.changeDescripcion}
                helperText={stateErrorForm.changeDescripcion}
                error={stateErrorForm.changeDescripcion && stateErrorForm.changeDescripcion !== ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography className="text-uppercase">
                INFO ADICIONAL Y VIDEO DEL ARTÍCULO
              </Typography>
              <Divider className="mb-2" />
              <Typography color="primary" variant="caption" component="p" className="mb-2">
                Puede añadir información adicional acerca artículo y un enlace al video del
                artículo.
              </Typography>
              <TextField
                type="text"
                name="additionalInfo"
                label="Información adicional"
                multiline
                rows={3}
                variant="outlined"
                fullWidth
                value={stateForm.additionalInfo}
                inputProps={productInputProps.additionalInfo}
                onChange={handleInputChange}
              />
              <div className="my-3"></div>
              <TextField
                type="text"
                name="videoUrl"
                label="Enlace al vídeo"
                variant="outlined"
                size="small"
                fullWidth
                value={stateForm.videoUrl}
                inputProps={productInputProps.videoUrl}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
          <SaveOrCancelButtons onSave={handleSave} onCancel={handleClose} loading={loading} />
        </Grid>
      </Grid>
      <Backdrop className={classes.backdrop} open={loading} onClick={handleClose}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  )
}

export default ProductForm
